const binaryOperations = {
  '==': (a, b) => a == b,
  '!=': (a, b) => a != b,
  '===': (a, b) => a === b,
  '!==': (a, b) => a !== b,
  '<': (a, b) => a < b,
  '<=': (a, b) => a <= b,
  '>': (a, b) => a > b,
  '>=': (a, b) => a >= b,
  '<<': (a, b) => a << b,
  '>>': (a, b) => a >> b,
  '>>>': (a, b) => a >>> b,
  '+': (a, b) => a + b,
  '-': (a, b) => a - b,
  '*': (a, b) => a * b,
  '/': (a, b) => a / b,
  '%': (a, b) => a % b,
  '**': (a, b) => a ** b,
  '|': (a, b) => a | b,
  '^': (a, b) => a ^ b,
  '&': (a, b) => a & b,
  'in': (a, b) => a in b,
  'instanceof': (a, b) => a instanceof b,
  //  '|>': (a, b) => a |> b,
};

// Each operation returns the assigned value, matching JS assignment
// expression semantics (so chained assignments like a = b = 1 work).
const assignmentOperations = {
  '=': (c, a, b) => c[a] = b,
  '+=': (c, a, b) => c[a] += b,
  '-=': (c, a, b) => c[a] -= b,
  '*=': (c, a, b) => c[a] *= b,
  '/=': (c, a, b) => c[a] /= b,
  '%=': (c, a, b) => c[a] %= b,
  '**=': (c, a, b) => c[a] **= b,
  '&&=': (c, a, b) => c[a] &&= b,
  '??=': (c, a, b) => c[a] ??= b,
  '||=': (c, a, b) => c[a] ||= b,
  '>>=': (c, a, b) => c[a] >>= b,
  '>>>=': (c, a, b) => c[a] >>>= b,
  '<<=': (c, a, b) => c[a] <<= b,
  '&=': (c, a, b) => c[a] &= b,
  '|=': (c, a, b) => c[a] |= b,
  '^=': (c, a, b) => c[a] ^= b,
};


const unaryOperators = {
  '-': a => -a,
  '+': a => +a,
  '!': a => !a,
  '~': a => ~a,
  'typeof': a => typeof a,
  'void': a => void a,
  // 'delete': a => delete a,
  'throw': a => { throw a; },
};

const updateOperators = (operator, prefix) => {
  if (operator === '++') {
    return (c, a) => prefix ? ++c[a] : c[a]++;
  } else if (operator === '--') {
    return (c, a) => prefix ? --c[a] : c[a]--;
  }
};

const callFunction = (node, context) => {
  const callee = evalNode(node.callee, context);
  if ((callee === void 0 || callee === null) &&
    (node.type === 'OptionalCallExpression' || node.callee.type === 'OptionalMemberExpression')) {
    return void 0;
  }
  const args = [];
  node.arguments.map(argument => {
    if (argument.type === 'SpreadElement') {
      args.push(...evalNode(argument, context));
    } else {
      args.push(evalNode(argument, context));
    }
  });
  const thisContext = getThisContext(context);
  return callee.apply(thisContext, args);
};

const nodeHandlers = {
  'NumericLiteral': (node, context) => node.value,
  'StringLiteral': (node, context) => node.value,
  'BooleanLiteral': (node, context) => node.value,
  'NullLiteral': (node, context) => null,

  'RegExpLiteral': (node, context) => new RegExp(node.pattern, node.flags),

  'ExpressionStatement': (node, context) => evalNode(node.expression, context),
  'BinaryExpression': (node, context) => binaryOperations[node.operator](evalNode(node.left, context), evalNode(node.right, context)),
  'AssignmentExpression': (node, context) => {
    // Support complex left-hand sides (e.g., obj.prop = 1, obj[expr] = 1)
    let obj, prop;
    if (node.left.type === 'MemberExpression' || node.left.type === 'OptionalMemberExpression') {
      obj = evalNode(node.left.object, context);
      prop = node.left.computed ? evalNode(node.left.property, context) : node.left.property.name;
    } else if (node.left.type === 'Identifier') {
      // Simple variable assignment
      obj = immediateContext(node.left, context);
      prop = node.left.name;
    } else {
      throw new Error('Unsupported assignment left-hand side');
    }
    return assignmentOperations[node.operator](obj, prop, evalNode(node.right, context));
  },
  'LogicalExpression': (node, context) => {
    const left = evalNode(node.left, context);
    if (node.operator === '&&') return left ? evalNode(node.right, context) : left;
    if (node.operator === '||') return left ? left : evalNode(node.right, context);
    if (node.operator === '??') return left ?? evalNode(node.right, context);
  },
  'UnaryExpression': (node, context) => unaryOperators[node.operator](evalNode(node.argument, context)),
  'UpdateExpression': (node, context) => {
    // Support complex left-hand sides (e.g., ++obj.prop, ++obj[expr])
    let obj, prop;
    if (node.argument.type === 'MemberExpression' || node.argument.type === 'OptionalMemberExpression') {
      obj = evalNode(node.argument.object, context);
      prop = node.argument.computed ? evalNode(node.argument.property, context) : node.argument.property.name;
    } else if (node.argument.type === 'Identifier') {
      obj = immediateContext(node.argument, context);
      prop = node.argument.name;
    } else {
      throw new Error('Unsupported update left-hand side');
    }
    return updateOperators(node.operator, node.prefix)(obj, prop);
  },
  'ConditionalExpression': (node, context) => {
    const test = evalNode(node.test, context);
    return test ? evalNode(node.consequent, context) : evalNode(node.alternate, context);
  },
  'MemberExpression': (node, context) => {
    const object = evalNode(node.object, context);
    const member = node.computed ? object[evalNode(node.property, context)] : object[node.property.name];
    if (typeof member === 'function') {
      return member.bind(object);
    }
    return member;
  },
  'OptionalMemberExpression': (node, context) => {
    const object = evalNode(node.object, context);
    if (object === void 0 || object === null) {
      return void 0;
    }
    const member = node.computed ? (object[evalNode(node.property, context)]) : (object[node.property.name]);
    if (typeof member === 'function') {
      return member.bind(object);
    }
    return member;
  },

  'ArrayExpression': (node, context) => {
    const arr = [];
    node.elements.map(elem => {
      if (elem.type === 'SpreadElement') {
        arr.push(...evalNode(elem, context));
      } else {
        arr.push(evalNode(elem, context));
      }
    });
    return arr;
  },
  'ObjectExpression': (node, context) => node.properties.reduce((acc, prop) => ({ ...acc, ...evalNode(prop, context) }), {}),
  'ObjectProperty': (node, context) => ({ [node.computed ? evalNode(node.key, context) : (node.key.name ?? node.key.value)]: evalNode(node.value, context) }),
  'SpreadElement': (node, context) => evalNode(node.argument, context),

  'Identifier': (node, context) => {
    if (Array.isArray(context)) {
      const hitContext = context.find(contextObj => node.name in contextObj);
      return hitContext ? hitContext[node.name] : undefined;
    } else if (typeof context === 'object') {
      return context[node.name];
    }
  },
  'ThisExpression': (node, context) => {
    return getThisContext(context);
  },

  'CallExpression': (node, context) => callFunction(node, context),
  'OptionalCallExpression': (node, context) => callFunction(node, context),
  'NewExpression': (node, context) => {
    const callee = evalNode(node.callee, context);
    const args = [];
    node.arguments.map(argument => {
      if (argument.type === 'SpreadElement') {
        args.push(...evalNode(argument, context));
      } else {
        args.push(evalNode(argument, context));
      }
    });
    return new callee(...args);
  },

  'Directive': (node, context) => evalNode(node.value, context),
  'DirectiveLiteral': (node, context) => node.value,
};

const getThisContext = (context) => {
  if (Array.isArray(context)) {
    const hitContext = context.find(contextObj => 'this' in contextObj);
    return hitContext ? hitContext['this'] : undefined;
  } else if (typeof context === 'object') {
    return context['this'];
  }
};

const immediateContext = (node, context) => {
  if (Array.isArray(context)) {
    if (context.length === 0) {
      return null;
    }
    const qualifiedContext = context.filter(contextObj => !(('$event' in contextObj && '$node' in contextObj) || 'this' in contextObj));
    return context.find(contextObj => node.name in contextObj) ?? qualifiedContext[0];
  } else if (typeof context === 'object') {
    return context;
  }
}

const evalNode = (node, context) => nodeHandlers[node.type](node, context);

// Component tag currently evaluating, set by lw-element so a failed
// expression can say where it came from.
let errorLabel = '';
const setErrorLabel = label => { errorLabel = label; };

const evaluate = (ast, context = {}, loc = {}) => {
  try {
    return ast.map(astNode => evalNode(astNode, context));
  } catch (e) {
    // A real Error, so the console shows a message and a stack instead of
    // "Uncaught [object Object]". The raw pieces ride along for tooling.
    const where = [errorLabel, loc?.startLine ? `line ${loc.startLine}` : ''].filter(Boolean).join(' ');
    const err = new Error(`[leanweb] expression failed${where ? ` (${where})` : ''}: ${e.message}`);
    err.cause = e;
    err.location = loc;
    err.ast = ast;
    err.context = context;
    throw err;
  }
};

export { evaluate, setErrorLabel };

//   module.exports = { evaluate };
// const parser = require('@babel/parser');
// const ast = parser.parse("name?.toUpperCase()").program.body;
// console.log(ast);
// const result = evaluate(JSON.parse(JSON.stringify(ast)), { name: 'hello' });
// console.log(result);